import React, { useState, useRef, useEffect } from 'react';
import { Play, Trash2, Terminal } from 'lucide-react';
import { Button } from './ui/button';
import { demoScenarios, aiToolkit } from '../data/mock';

const InteractiveDemo = () => {
  const [lines, setLines] = useState([]);
  const [running, setRunning] = useState(null);
  const timersRef = useRef([]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((id) => clearTimeout(id));
    };
  }, []);

  const clearTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };

  const runScenario = (key) => {
    clearTimers();
    setRunning(key);
    setLines([]);
    const scenario = demoScenarios[key];
    scenario.log.forEach(({ t, line }) => {
      const id = setTimeout(() => {
        setLines((prev) => [...prev, line]);
      }, t);
      timersRef.current.push(id);
    });
    const doneAt = Math.max(...scenario.log.map((l) => l.t)) + 400;
    const doneId = setTimeout(() => setRunning(null), doneAt);
    timersRef.current.push(doneId);
  };

  const clearLog = () => {
    clearTimers();
    setLines([]);
    setRunning(null);
  };

  return (
    <section
      id="demo"
      className="py-20 md:py-24 px-6 border-b border-stone-400/25 dark:border-stone-500/20 bg-white/[0.22] dark:bg-stone-950/30"
    >
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <h2 className="text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4 leading-tight">
            Interactive demo
          </h2>
          <div className="w-16 h-1 bg-amber-800 dark:bg-amber-600 rounded-full mb-4" />
          <p className="text-base text-stone-600 dark:text-stone-400 max-w-2xl">
            Simulated runner output—how I narrate API and failure paths. No backend required.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={() => runScenario('login')}
                disabled={running !== null}
                className="bg-stone-900 hover:bg-stone-800 dark:bg-stone-100 dark:hover:bg-stone-200 text-white dark:text-stone-900"
              >
                <Play className="w-4 h-4 mr-2" />
                Run sample API test
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => runScenario('payment')}
                disabled={running !== null}
                className="border-2 border-stone-300 dark:border-stone-600"
              >
                <Play className="w-4 h-4 mr-2" />
                Payment failure
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={clearLog} className="text-stone-600">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear
              </Button>
            </div>

            <div
              className="rounded-2xl border border-stone-800 dark:border-stone-600 bg-stone-950 text-stone-100 font-mono text-xs md:text-sm p-4 min-h-[280px] shadow-inner overflow-hidden"
              role="log"
              aria-live="polite"
            >
              <div className="flex items-center gap-2 text-stone-500 mb-3 pb-2 border-b border-stone-800">
                <Terminal className="w-4 h-4" />
                <span>sample-runner — {running ? 'running…' : 'idle'}</span>
              </div>
              {lines.length === 0 && (
                <p className="text-stone-500">Run a scenario to see PASS / FAIL lines.</p>
              )}
              <ul className="space-y-1.5">
                {lines.map((line, i) => (
                  <li
                    key={`${i}-${line}`}
                    className={
                      line.includes('[FAIL]')
                        ? 'text-red-400'
                        : line.includes('[PASS]')
                          ? 'text-emerald-400'
                          : line.includes('[DONE]')
                            ? 'text-amber-300'
                            : 'text-stone-400'
                    }
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-sm font-bold text-stone-900 dark:text-stone-100 uppercase tracking-wider">
              AI angle (workflow)
            </h3>
            {aiToolkit.map((tool) => (
              <div
                key={tool.title}
                className="p-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-white/70 dark:bg-stone-800/60"
              >
                <p className="font-semibold text-stone-900 dark:text-stone-100 text-sm mb-1">{tool.title}</p>
                <p className="text-xs text-stone-600 dark:text-stone-400 mb-2">{tool.description}</p>
                <p className="text-[11px] font-mono text-stone-500 dark:text-stone-500 leading-relaxed border-l-2 border-amber-700/50 pl-2">
                  {tool.example}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemo;
